import { useState } from 'react';
import Card from '../base/Card';
import Button from '../base/Button';
import { useNavigate } from 'react-router-dom';

interface Listing {
  id: number;
  name: string;
  location: string;
  price: string;
  rating: number;
  type: string;
  features: string[];
  image: string;
  publisher: string;
  publisherAvatar: string;
  state: string;
}

// 🔧 PROPS DEL COMPONENTE
interface ListingsSectionProps {
  listings?: Listing[]; // Datos de la API (opcional)
  loading?: boolean; // Estado de carga
  error?: string | null; // Error de la API
}

export default function ListingsSection({ 
  listings: apiListings, 
  loading = false, 
  error = null 
}: ListingsSectionProps) {
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Datos de ejemplo como respaldo
  const fallbackListings: Listing[] = [
    {
      id: 1,
      name: 'Cancha San Rafael',
      location: 'Ciudad de México, Roma Norte',
      price: '$280/hora',
      rating: 4.7,
      type: 'Fútbol 7',
      features: ['Césped Sintético', 'Iluminación LED', 'Estacionamiento'],
      image: 'https://readdy.ai/api/search-image?query=modern%20football%20field%20with%20synthetic%20grass%2C%20professional%20soccer%20pitch%20with%20LED%20lighting%2C%20green%20artificial%20turf%20with%20white%20lines%2C%20sports%20facility%20with%20parking%20area%20and%20red%20architectural%20elements&width=350&height=200&seq=listing1&orientation=landscape',
      publisher: 'Juan Carlos López',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20male%20user%20profile%20photo%2C%20casual%20friendly%20appearance%2C%20young%20adult%2C%20clean%20background&width=50&height=50&seq=publisher1&orientation=squarish',
      state: 'Ciudad de México'
    },
    {
      id: 2,
      name: 'Estadio La Cantera',
      location: 'Guadalajara, Zapopan',
      price: '$450/hora',
      rating: 4.9,
      type: 'Fútbol 11',
      features: ['Césped Natural', 'Vestidores', 'Tribunas'],
      image: 'https://readdy.ai/api/search-image?query=professional%20soccer%20stadium%20with%20natural%20grass%2C%20football%20field%20with%20bleachers%20and%20changing%20rooms%2C%20green%20natural%20turf%2C%20stadium%20with%20red%20and%20white%20colors%2C%20sports%20complex&width=350&height=200&seq=listing2&orientation=landscape',
      publisher: 'Ana Martínez',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20female%20user%20profile%20photo%2C%20friendly%20smile%2C%20young%20adult%2C%20casual%20clothing%2C%20clean%20background&width=50&height=50&seq=publisher2&orientation=squarish',
      state: 'Jalisco'
    },
    {
      id: 3,
      name: 'Complejo Deportivo Norte',
      location: 'Monterrey, San Pedro',
      price: '$320/hora',
      rating: 4.6,
      type: 'Fútbol 7',
      features: ['Aire Acondicionado', 'Cafetería', 'Wi-Fi'],
      image: 'https://readdy.ai/api/search-image?query=indoor%20soccer%20facility%20with%20air%20conditioning%2C%20modern%20football%20complex%20with%20cafeteria%2C%20artificial%20turf%20field%20with%20green%20surface%2C%20sports%20center%20with%20red%20and%20white%20branding&width=350&height=200&seq=listing3&orientation=landscape',
      publisher: 'Carlos Ruiz',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20male%20user%20profile%20photo%2C%20middle%20aged%2C%20business%20casual%2C%20professional%20appearance&width=50&height=50&seq=publisher3&orientation=squarish',
      state: 'Nuevo León'
    },
    {
      id: 4,
      name: 'Arena Fútbol Plus',
      location: 'Puebla, Angelópolis',
      price: '$380/hora',
      rating: 4.8,
      type: 'Fútbol 11',
      features: ['Césped Híbrido', 'Sonido', 'Seguridad 24h'],
      image: 'https://readdy.ai/api/search-image?query=premium%20soccer%20field%20with%20hybrid%20grass%2C%20football%20stadium%20with%20sound%20system%20and%20security%2C%20professional%20green%20pitch%20with%20white%20markings%2C%20modern%20sports%20facility&width=350&height=200&seq=listing4&orientation=landscape',
      publisher: 'Laura Sánchez',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20female%20user%20profile%20photo%2C%20professional%20woman%2C%20friendly%20expression%2C%20business%20attire&width=50&height=50&seq=publisher4&orientation=squarish',
      state: 'Puebla'
    },
    {
      id: 5,
      name: 'Cancha Los Pinos',
      location: 'León, Centro',
      price: '$200/hora',
      rating: 4.4,
      type: 'Fútbol 5',
      features: ['Techada', 'Regaderas', 'Renta de Balones'],
      image: 'https://readdy.ai/api/search-image?query=covered%20small%20soccer%20field%2C%20indoor%20futsal%20court%20with%20showers%2C%20small%20football%20pitch%20with%20ball%20rental%20service%2C%20green%20synthetic%20surface%20with%20white%20lines&width=350&height=200&seq=listing5&orientation=landscape',
      publisher: 'Roberto Fernández',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20male%20user%20profile%20photo%2C%20friendly%20smile%2C%20sports%20enthusiast%2C%20casual%20attire&width=50&height=50&seq=publisher5&orientation=squarish',
      state: 'Guanajuato'
    },
    {
      id: 6,
      name: 'Deportivo Águilas',
      location: 'Tijuana, Zona Río',
      price: '$350/hora',
      rating: 4.7,
      type: 'Fútbol 7',
      features: ['Pasto Natural', 'Bancas', 'Área VIP'],
      image: 'https://readdy.ai/api/search-image?query=soccer%20field%20with%20natural%20grass%20and%20benches%2C%20football%20pitch%20with%20VIP%20area%2C%20green%20natural%20turf%20with%20professional%20goalposts%2C%20sports%20facility%20with%20red%20and%20white%20details&width=350&height=200&seq=listing6&orientation=landscape',
      publisher: 'María González',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20female%20user%20profile%20photo%2C%20confident%20business%20woman%2C%20professional%20headshot&width=50&height=50&seq=publisher6&orientation=squarish',
      state: 'Baja California'
    },
    {
      id: 7,
      name: 'Centro Deportivo Elite',
      location: 'Querétaro, Juriquilla',
      price: '$400/hora',
      rating: 4.9,
      type: 'Fútbol 11',
      features: ['Césped Premium', 'VAR', 'Transmisión'],
      image: 'https://readdy.ai/api/search-image?query=elite%20soccer%20stadium%20with%20premium%20grass%2C%20professional%20football%20field%20with%20VAR%20technology%2C%20broadcasting%20facilities%2C%20green%20premium%20turf%2C%20modern%20sports%20complex&width=350&height=200&seq=listing7&orientation=landscape',
      publisher: 'Diego Ramírez',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20male%20user%20profile%20photo%2C%20young%20entrepreneur%2C%20modern%20casual%20style&width=50&height=50&seq=publisher7&orientation=squarish',
      state: 'Querétaro'
    },
    {
      id: 8,
      name: 'Cancha Rápida FC',
      location: 'Mérida, Norte',
      price: '$250/hora',
      rating: 4.5,
      type: 'Fútbol 5',
      features: ['Techada', 'Climatizada', 'Snacks'],
      image: 'https://readdy.ai/api/search-image?query=climate%20controlled%20indoor%20soccer%20field%2C%20covered%20futsal%20court%20with%20snack%20bar%2C%20small%20football%20pitch%20with%20air%20conditioning%2C%20green%20synthetic%20surface&width=350&height=200&seq=listing8&orientation=landscape',
      publisher: 'Patricia Morales',
      publisherAvatar: 'https://readdy.ai/api/search-image?query=mexican%20female%20user%20profile%20photo%2C%20friendly%20business%20owner%2C%20warm%20smile&width=50&height=50&seq=publisher8&orientation=squarish',
      state: 'Yucatán'
    }
  ];

  // 🔧 USA DATOS DE LA API O DATOS DE RESPALDO
  const listings = apiListings || fallbackListings;

  const states = [
    'Todos los estados',
    'Aguascalientes',
    'Baja California',
    'Baja California Sur',
    'Campeche',
    'Chiapas',
    'Chihuahua',
    'Ciudad de México',
    'Coahuila',
    'Colima',
    'Durango',
    'Guanajuato',
    'Guerrero',
    'Hidalgo',
    'Jalisco',
    'México',
    'Michoacán',
    'Morelos',
    'Nayarit',
    'Nuevo León',
    'Oaxaca',
    'Puebla',
    'Querétaro',
    'Quintana Roo',
    'San Luis Potosí',
    'Sinaloa',
    'Sonora',
    'Tabasco',
    'Tamaulipas',
    'Tlaxcala',
    'Veracruz',
    'Yucatán',
    'Zacatecas'
  ];

  const categories = [
    'Todas las categorías',
    'Fútbol 11',
    'Fútbol 7',
    'Fútbol 5',
    'Futsal',
    'Fútbol Playa'
  ];

  const filteredListings = listings.filter(listing => {
    const matchesState = selectedState === '' || selectedState === 'Todos los estados' || listing.state === selectedState;
    const matchesCategory = selectedCategory === '' || selectedCategory === 'Todas las categorías' || listing.type === selectedCategory;
    return matchesState && matchesCategory;
  });

  const handleViewDetails = (id: number) => {
    navigate(`/anuncio/${id}`);
  };

  // 🔧 MOSTRAR ESTADO DE CARGA
  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-green-600"></div>
          </div>
        </div>
      </section>
    );
  }

  // 🔧 MOSTRAR ERROR SI EXISTE
  if (error) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <i className="ri-error-warning-line text-6xl text-red-500 mb-4"></i>
            <p className="text-red-600 text-lg mb-2">Error al cargar los anuncios</p>
            <p className="text-gray-500">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Todos los Anuncios
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Explora todas las canchas disponibles y encuentra la perfecta para ti
          </p>

          {/* Filtros */}
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="relative">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="appearance-none border-2 border-green-600 rounded-lg px-6 py-3 pr-12 text-gray-700 font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer bg-white"
              >
                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-green-600 pointer-events-none text-xl"></i>
            </div>

            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none border-2 border-green-600 rounded-lg px-6 py-3 pr-12 text-gray-700 font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent cursor-pointer bg-white"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-green-600 pointer-events-none text-xl"></i>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredListings.map((listing) => (
            <Card key={listing.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative">
                <img
                  src={listing.image}
                  alt={listing.name}
                  className="w-full h-40 object-cover object-top"
                />
                <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-medium">
                  {listing.type}
                </div>
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-1">
                  <i className="ri-heart-line text-gray-600 hover:text-red-500 cursor-pointer"></i>
                </div>
              </div>
              
              <div className="p-4">
                {/* Publicado por */}
                <div className="flex items-center space-x-2 mb-3 pb-3 border-b border-gray-200">
                  <img
                    src={listing.publisherAvatar}
                    alt={listing.publisher}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Publicado por</p>
                    <p className="text-sm font-medium text-gray-700 truncate">{listing.publisher}</p>
                  </div>
                </div>

                <h3 className="font-bold text-lg mb-1">{listing.name}</h3>
                <div className="flex items-center text-gray-600 mb-2">
                  <i className="ri-map-pin-line mr-1 text-sm"></i>
                  <span className="text-sm">{listing.location}</span>
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <i className="ri-star-fill text-yellow-400 mr-1 text-sm"></i>
                    <span className="text-sm font-medium">{listing.rating}</span>
                  </div>
                  <span className="text-green-600 font-bold">{listing.price}</span>
                </div>

                <div className="mb-3">
                  <div className="flex flex-wrap gap-1">
                    {listing.features.slice(0, 2).map((feature, index) => (
                      <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                        {feature}
                      </span>
                    ))}
                    {listing.features.length > 2 && (
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                        +{listing.features.length - 2}
                      </span>
                    )}
                  </div>
                </div>

                <Button 
                  className="w-full" 
                  size="sm"
                  onClick={() => handleViewDetails(listing.id)}
                >
                  Ver Detalles
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {filteredListings.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 text-lg">No se encontraron canchas con los filtros seleccionados</p>
          </div>
        )}

        <div className="text-center mt-12">
          <Button variant="outline" size="lg" className="px-8">
            Ver Más Anuncios
          </Button>
        </div>
      </div>
    </section>
  );
}
